/*
 |------------------------------------------------------------------------------
 | Options utility
 |------------------------------------------------------------------------------
 |
 | This module allows for clear communication between the page and JavaScript,
 | which allows for interaction between frontend and backend packed in
 | an easy to use API.
 |
 | Just put a script tag on the page with type="json/options" and JSON inside
 | and these properties will automatically be available in the options object.
 |
 */

/* ========================================================================== *\
	IMPORTS
\* ========================================================================== */

import { isNil, isObject } from 'lodash';
/* == IMPORTS =============================================================== */



/* ========================================================================== *\
	PRIVATE VARIABLES
\* ========================================================================== */

/**
 * The locally scoped object to set options on
 * @type {Object}
 */
let propHolder = {},

	/**
	 * The locally scoped options
	 * @type {Object}
	 */
	localOptions = {};

/* == PRIVATE VARIABLES ===================================================== */

/**
 * Test if a property is set
 *
 * @param {String} property The property to check on
 */
function isSet(property) {
	return propHolder[property] !== undefined;
}

/**
 * Sets an options property
 *
 * @param {String} property The value to set
 * @param {*} [value] The value to set it to
 * @return {*} The value that is set
 */
function set(property, value) {
	// If it is an object
	if (isObject(property)) {
		// Merge it with any possible properties
		// that are already set on this key
		$.extend(true, propHolder, property);

		return;
	}

	// Set the value for the property
	propHolder[property] = value;

	// Return the property that has been set
	return propHolder[property];
}

/**
 * Gets an options property
 *
 * @param  {String} property The property to get
 * @param  {String} defaultValue A default value
 * @return {*} The property
 */
function get(property, defaultValue) {
	if (isSet(property)) {
		return propHolder[property];
	}

	propHolder[property] = defaultValue;

	return propHolder[property];
}

/**
 *
 * @param {String} path The property path within the translations object.
 *
 * @returns {(null|string)} When the path couldn't be resolved the method
 *          returns null; otherwise it will return the value stored at the
 *          specified path.
 *
 * @example
 * // This will return the value 'baz translation' assigned to the baz property
 * // for this Object:
 * // translations: {
 * //   foo: {
 * //     bar: {
 * //       baz: 'baz translation'
 * //     }
 * //   }
 * // }
 * getTranslation('foo.bar.baz');
 */
function getTranslation(path, translationsRoot = 'translations') {
	return getPath(`${translationsRoot}.${path}`);
}

/**
 * Delete an options property
 *
 * @param  {String} property The property to delete
 * @param  {Boolean} strictMode Whether or not to throw an exception
 */
function remove(property, strictMode) {
	if (isSet(property)) {
		delete propHolder[property];
	}
}

/**
 * Extend the localOptions object with the given options object
 *
 * @param {Object} extendOptions The objects to extend with
 */
function extendLocalOptions(extendOptions) {
	localOptions = $.extend(true, localOptions, extendOptions);
}

/**
 * Parse the options of a options block and add to the global options
 */
function parseOptionsBlock() {
	var $block = $(this),
		blockOptions = JSON.parse($block.html());

	extendLocalOptions(blockOptions);
}

/**
 * Initializes op a script tag with type="text/options"
 * Gets invoked immediately!
 */
function getPageOptions() {
	// Get all script
	var $script = $('script[type="json/options"]');

	if (!$script.length) {
		return;
	}

	// Parse all script blocks
	$script.each(parseOptionsBlock);

	set(localOptions);
}

/**
 * Returns an option value based on its path.
 *
 * @param {String} path The property path within the translations object.
 *
 * @returns {(null|string)} When the path couldn't be resolved the method
 *          returns null; otherwise it will return the value stored at the
 *          specified path.
 *
 * @example
 * // This will return the value 'baz translation' assigned to the baz property
 * // for this Object:
 * // translations: {
 * //   foo: {
 * //     bar: {
 * //       baz: 'baz translation'
 * //     }
 * //   }
 * // }
 * getTranslation('foo.bar.baz');
 */
function getPath(path) {
	// Split the path into its individual components and remove the first part
	// from the resulting array.
	const
		parts = path.split('.'),
		startPart = parts.shift();

	let
		partValue = get(startPart);

	// Iterate over the path parts until we've processed all of them or the
	// current part value is null.
	while (
		parts.length > 0 &&
		!isNil(partValue)
	) {
		// Remove the part at index 0.
		const
			part = parts.shift();

		// Get the value for the current part.
		partValue = partValue[part];
	}

	// Return the part value, make sure to return null when the value is null or
	// when it is undefined.
	return isNil(partValue)
		? null
		: partValue;
}


/* ========================================================================== *\
	INITIALIZATION
\* ========================================================================== */

// Get page options when the DOM is loaded
// using a short hand document ready
getPageOptions();

/* == INITIALIZATION ======================================================== */



/* ========================================================================== *\
	EXPORTS
\* ========================================================================== */

/**
 * Expose public API
 */
export {
	get,
	getPageOptions,
	getPath,
	getTranslation,
	isSet,
	remove,
	set
};

/* == EXPORTS =============================================================== */